import { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Dialog } from '../../Surfaces/Dialog';
import { Box as LayoutBox } from '../../Layout/Box';
import { Typography } from '../../Typography';
import { Button } from '../../Button/button';
import { CloseButton } from '../../Buttons/CloseButton';
import { CircularIconButton } from '../../Buttons/CircularIconButton/CircularIconButton';

export interface PhotoModalPhoto {
  /** Photo name/filename */
  name: string;
  /** Image source - can be URL, base64 data, File object, or null for empty state */
  imageSrc: string | File | null;
  /** Optional photo ID */
  id?: string;
  /** Optional date to display */
  date?: string | Date | number;
}

/** Optional labels for i18n (defaults to English). */
export interface PhotoModalLabels {
  /** Close button aria-label and confirm dialog cancel */
  close?: string;
  /** Delete photo button aria-label and confirm dialog title */
  deletePhoto?: string;
  /** Confirm delete dialog message */
  deletePhotoPrompt?: string;
  /** Error message when image fails to load */
  failedToLoadImage?: string;
  /** Cancel button in confirm dialog */
  cancel?: string;
}

const DEFAULT_LABELS: Required<PhotoModalLabels> = {
  close: 'Close',
  deletePhoto: 'Delete photo',
  deletePhotoPrompt: 'Are you sure you want to delete this photo?',
  failedToLoadImage: 'Failed to load image',
  cancel: 'Cancel',
};

export interface PhotoModalProps {
  /** The photo to display (null to close) */
  photo: PhotoModalPhoto | null;
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Optional function to format the date */
  formatDate?: (date: string | Date | number) => string;
  /** Optional callback when delete is confirmed */
  onDelete?: (photo: PhotoModalPhoto) => Promise<void>;
  /** Optional callback when delete fails (e.g. to show snackbar) */
  onDeleteError?: (error: unknown) => void;
  /** Whether deletion is in progress */
  isDeleting?: boolean;
  /** Optional labels for accessibility and confirm dialog (defaults to English) */
  labels?: PhotoModalLabels;
  /** Optional aria label for the modal */
  ariaLabelledBy?: string;
  /** Optional test ID for the delete button */
  deleteButtonTestId?: string;
  /** Optional test ID for the confirm delete button */
  confirmDeleteButtonTestId?: string;
}

const modalContentSx = {
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '90vw',
  maxHeight: '90vh',
  outline: 'none',
  position: 'relative',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '10px',
  overflow: 'hidden',
  '&.MuiPaper-rounded': {
    borderRadius: '10px',
  },
};

const imageContainerSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  minHeight: '200px',
  position: 'relative',
  pt: '50px',
  width: '100%',
  mx: 0,
  px: 0,
  overflow: 'hidden',
};

const imageSx = {
  width: '100%',
  maxHeight: '500px',
  objectFit: 'contain',
  borderRadius: '10px',
};

const loadingContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 2,
  minHeight: '200px',
};

/**
 * Reusable PhotoModal component for displaying full-size photos in a dialog.
 *
 * Supports flexible image sources (URL, base64, or File). Uses the library Dialog
 * for the main view and for the delete confirmation dialog. Optional delete
 * flow with confirm dialog; labels are configurable for i18n.
 *
 * @example Single photo
 * ```tsx
 * <PhotoModal
 *   photo={photo ? { name: photo.fileName, imageSrc: photo.url } : null}
 *   open={!!photo}
 *   onClose={() => setPhoto(null)}
 * />
 * ```
 *
 * @example With delete
 * ```tsx
 * <PhotoModal
 *   photo={selectedPhoto ? { name: selectedPhoto.name, imageSrc: selectedPhoto.url } : null}
 *   open={!!selectedPhoto}
 *   onClose={handleClose}
 *   onDelete={handleDelete}
 *   onDeleteError={(err) => enqueueSnackbar('Delete failed', { variant: 'error' })}
 *   labels={{ deletePhoto: t('photos.delete'), deletePhotoPrompt: t('photos.confirm_delete') }}
 * />
 * ```
 */
export function PhotoModal({
  photo,
  open,
  onClose,
  formatDate,
  onDelete,
  onDeleteError,
  isDeleting = false,
  labels: labelsProp,
  ariaLabelledBy = 'photo-modal-title',
  deleteButtonTestId = 'photo_delete_button',
  confirmDeleteButtonTestId = 'photo_confirm_delete_button',
}: PhotoModalProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');

  const handleDialogClose = useCallback(
    (_event: object) => {
      onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (photo) {
      setIsLoading(true);
      setHasError(false);

      if (photo.imageSrc === null) {
        // Handle null imageSrc gracefully - show empty state (no image, but modal still displays name/date)
        setImageUrl('');
        setIsLoading(false);
        setHasError(false);
      } else if (typeof photo.imageSrc === 'string') {
        setImageUrl(photo.imageSrc);
      } else if (photo.imageSrc instanceof File) {
        const url = URL.createObjectURL(photo.imageSrc);
        setImageUrl(url);
        return () => {
          URL.revokeObjectURL(url);
        };
      } else {
        // Fallback for unexpected types
        setImageUrl('');
        setIsLoading(false);
        setHasError(false);
      }
    } else {
      setImageUrl('');
    }
  }, [photo]);

  useEffect(() => {
    if (!open || !photo) {
      setIsDeleteDialogOpen(false);
      setIsSubmitting(false);
    }
  }, [open, photo]);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    if (isSubmitting || isDeleting) {
      return;
    }
    setIsDeleteDialogOpen(false);
    setIsSubmitting(false);
  }, [isSubmitting, isDeleting]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!photo || !onDelete) {
      setIsDeleteDialogOpen(false);
      return;
    }
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onDelete(photo);
      setIsDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      onDeleteError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [photo, onDelete, onClose, onDeleteError, isSubmitting]);

  const displayDate = photo?.date
    ? formatDate
      ? formatDate(photo.date)
      : typeof photo.date === 'number'
      ? new Date(photo.date).toLocaleString()
      : String(photo.date)
    : null;

  const mainDialogContent = photo && (
    <LayoutBox sx={{ position: 'relative', width: '100%', mx: 0, px: 0 }}>
      <CloseButton
        onClick={onClose}
        aria-label={labels.close}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
          backgroundColor: 'common.midGray',
          color: 'common.white',
        }}
      />
      <LayoutBox sx={imageContainerSx}>
        {isLoading && (
          <LayoutBox sx={loadingContainerSx}>
            <CircularProgress size={40} />
          </LayoutBox>
        )}
        {hasError && (
          <LayoutBox sx={loadingContainerSx}>
            <Typography variant="body2" color="error">
              {labels.failedToLoadImage}
            </Typography>
          </LayoutBox>
        )}
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt={photo.name}
            sx={{
              ...imageSx,
              display: isLoading || hasError ? 'none' : 'block',
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
      </LayoutBox>
      <Typography id={ariaLabelledBy} variant="h2" sx={{ pl: 2, pt: 1, flexShrink: 0 }}>
        {photo.name}
      </Typography>
      {displayDate && (
        <Typography variant="body1" sx={{ pl: 2, flexShrink: 0 }}>
          {displayDate}
        </Typography>
      )}
      {onDelete && (
        <CircularIconButton
          onClick={handleDeleteClick}
          color="error"
          disabled={isDeleting || isDeleteDialogOpen}
          sx={{ m: 2, flexShrink: 0 }}
          aria-label={labels.deletePhoto}
          data-testid={deleteButtonTestId}
        >
          {isDeleting && isDeleteDialogOpen ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <DeleteIcon />
          )}
        </CircularIconButton>
      )}
    </LayoutBox>
  );

  return (
    <>
      {photo && (
        <Dialog
          open={open}
          onClose={handleDialogClose}
          showCloseButton={false}
          content={mainDialogContent}
          aria-labelledby={ariaLabelledBy}
          paperSx={modalContentSx}
          contentSx={{ p: 0 }}
        />
      )}
      {onDelete && (
        <Dialog
          open={isDeleteDialogOpen}
          onClose={handleDeleteCancel}
          title={labels.deletePhoto}
          content={<Typography variant="body2">{labels.deletePhotoPrompt}</Typography>}
          actions={
            <>
              <Button
                color="error"
                shape="pill"
                onClick={handleDeleteConfirm}
                disabled={isDeleting || isSubmitting}
                data-testid={confirmDeleteButtonTestId}
              >
                {labels.deletePhoto}
              </Button>
              <Button
                variant="text"
                color="inherit"
                shape="pill"
                onClick={handleDeleteCancel}
                disabled={isSubmitting || isDeleting}
              >
                {labels.cancel}
              </Button>
            </>
          }
        />
      )}
    </>
  );
}

PhotoModal.displayName = 'PhotoModal';
